import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { 
  Shield, 
  AlertTriangle, 
  LogOut, 
  Smartphone, 
  Clock, 
  CheckCircle,
  X,
  Loader2
} from 'lucide-react';
import { activityService } from '@/services/activity.service';
import toast from 'react-hot-toast';

interface SecurityActionsProps {
  className?: string;
}

export default function SecurityActions({ className = '' }: SecurityActionsProps) {
  const queryClient = useQueryClient();
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [revokeType, setRevokeType] = useState<'others' | 'all'>('others');

  // Fetch activity stats for security overview
  const { data: stats } = useQuery({
    queryKey: ['activityStats'],
    queryFn: () => activityService.getActivityStats()
  });

  // Fetch suspicious activity check
  const { data: suspiciousActivity } = useQuery({
    queryKey: ['suspiciousActivity'],
    queryFn: () => activityService.checkSuspiciousActivity()
  });

  // Revoke tokens mutation
  const revokeTokensMutation = useMutation({
    mutationFn: (type: 'others' | 'all') => 
      type === 'others' 
        ? activityService.revokeOtherSessions('User requested logout from other devices')
        : activityService.emergencyLogout('User requested emergency logout'),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['userActivity'] });
      queryClient.invalidateQueries({ queryKey: ['activityStats'] });
      queryClient.invalidateQueries({ queryKey: ['suspiciousActivity'] });
      
      toast.success(
        revokeType === 'all' 
          ? 'All sessions revoked. You will be logged out shortly.'
          : `Successfully logged out from ${data.revoked_tokens_count} other devices.`
      );
      
      setShowRevokeModal(false);
      
      // If all tokens were revoked, redirect to login after a short delay
      if (revokeType === 'all') {
        setTimeout(() => {
          window.location.href = '/login';
        }, 2000);
      }
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to revoke sessions. Please try again.');
    }
  });

  const handleRevokeTokens = () => {
    revokeTokensMutation.mutate(revokeType);
  };

  return (
    <>
      <div className={`card p-0 sm:p-6 ${className}`}>
        <div className="p-4 sm:p-6 border-b">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Shield className="w-5 h-5 text-blue-600" />
            Account Security
          </h3>
        </div>

        <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
          {/* Security Overview */}
          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            <div className="bg-green-50 rounded-lg p-3 sm:p-4 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                <span className="text-sm sm:text-base font-medium text-green-900">Recent Logins</span>
              </div>
              <p className="text-xl sm:text-2xl font-bold text-green-700">
                {stats?.recentLogins || 0}
              </p>
              <p className="text-sm text-green-600">Last 30 days</p>
            </div>

            <div className={`rounded-lg p-3 sm:p-4 min-w-0 ${
              (stats?.failedAttempts || 0) > 0 ? 'bg-red-50' : 'bg-gray-50'
            }`}>
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className={`w-5 h-5 flex-shrink-0 ${
                  (stats?.failedAttempts || 0) > 0 ? 'text-red-600' : 'text-gray-600'
                }`} />
                <span className={`text-sm sm:text-base font-medium ${
                  (stats?.failedAttempts || 0) > 0 ? 'text-red-900' : 'text-gray-900'
                }`}>
                  Failed Attempts
                </span>
              </div>
              <p className={`text-xl sm:text-2xl font-bold ${
                (stats?.failedAttempts || 0) > 0 ? 'text-red-700' : 'text-gray-700'
              }`}>
                {stats?.failedAttempts || 0}
              </p>
              <p className={`text-sm ${
                (stats?.failedAttempts || 0) > 0 ? 'text-red-600' : 'text-gray-600'
              }`}>
                Recent failures
              </p>
            </div>
          </div>

          {/* Last Login Info */}
          {stats?.lastLogin && (
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-5 h-5 text-blue-600" />
                <span className="font-medium text-blue-900">Last Login</span>
              </div>
              <p className="text-sm text-blue-700">
                {new Date(stats.lastLogin).toLocaleString()}
              </p>
            </div>
          )}

          {/* Suspicious Activity Warning */}
          {suspiciousActivity?.hasSuspiciousActivity && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-red-900 mb-1">Suspicious Activity Detected</h4>
                  <p className="text-sm text-red-700 mb-3">
                    We detected {suspiciousActivity.failedLoginCount} failed login attempts. 
                    Consider revoking all sessions if this wasn't you.
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setRevokeType('all');
                        setShowRevokeModal(true);
                      }}
                      className="text-xs bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700 transition-colors"
                    >
                      Secure Account Now
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Security Actions */}
          <div className="space-y-3">
            <h4 className="font-medium text-gray-900">Session Management</h4>
            
            <div className="space-y-2">
              <button
                onClick={() => {
                  setRevokeType('others');
                  setShowRevokeModal(true);
                }}
                className="w-full flex items-center justify-between gap-3 p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors text-left"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <Smartphone className="w-5 h-5 text-gray-600 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-gray-900">Sign Out Other Devices</p>
                    <p className="text-sm text-gray-600">Keep this session, end all others</p>
                  </div>
                </div>
                <div className="flex-shrink-0 text-xs sm:text-sm text-gray-500">Recommended</div>
              </button>

              <button
                onClick={() => {
                  setRevokeType('all');
                  setShowRevokeModal(true);
                }}
                className="w-full flex items-center justify-between gap-3 p-3 bg-red-50 hover:bg-red-100 rounded-lg transition-colors text-left"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <LogOut className="w-5 h-5 text-red-600 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-red-900">Emergency Logout</p>
                    <p className="text-sm text-red-600">Sign out from all devices including this one</p>
                  </div>
                </div>
                <div className="flex-shrink-0 text-xs sm:text-sm text-red-500">Extreme</div>
              </button>
            </div>
          </div>

          {/* Recent IPs */}
          {suspiciousActivity?.unusualIpAddresses && suspiciousActivity.unusualIpAddresses.length > 0 && (
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Recent IP Addresses</h4>
              <div className="space-y-1">
                {suspiciousActivity.unusualIpAddresses.slice(0, 3).map((ip, index) => (
                  <div key={index} className="flex items-center gap-2 text-sm text-gray-600">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span className="font-mono">{ip}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Revoke Tokens Modal */}
      {showRevokeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  {revokeType === 'all' ? 'Emergency Logout' : 'Sign Out Other Devices'}
                </h3>
                <button
                  onClick={() => setShowRevokeModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="flex items-start gap-3 mb-4">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  revokeType === 'all' ? 'bg-red-100' : 'bg-yellow-100'
                }`}>
                  {revokeType === 'all' ? (
                    <LogOut className="w-6 h-6 text-red-600" />
                  ) : (
                    <Shield className="w-6 h-6 text-yellow-600" />
                  )}
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-1">
                    {revokeType === 'all' 
                      ? 'This will sign you out from all devices'
                      : 'This will sign you out from other devices'
                    }
                  </h4>
                  <p className="text-sm text-gray-600">
                    {revokeType === 'all'
                      ? 'You will need to sign in again on this device. This action cannot be undone.'
                      : 'Your current session will remain active, but all other sessions will be terminated.'
                    }
                  </p>
                </div>
              </div>

              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowRevokeModal(false)}
                  disabled={revokeTokensMutation.isPending}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRevokeTokens}
                  disabled={revokeTokensMutation.isPending}
                  className={`px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2 ${
                    revokeType === 'all' 
                      ? 'bg-red-600 hover:bg-red-700' 
                      : 'bg-yellow-600 hover:bg-yellow-700'
                  }`}
                >
                  {revokeTokensMutation.isPending && (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  )}
                  {revokeType === 'all' ? 'Sign Out All' : 'Sign Out Others'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}